/**
 * Persistent ring-buffer logger for the auth flow.
 *
 * Why it exists: forced sign-outs redirect to /login before anyone has a
 * chance to open devtools, so plain console.log is useless for catching
 * this in production. Every entry also lands in localStorage, which
 * survives the redirect/reload, so the sequence of events leading up to
 * a boot-off can be reconstructed after the fact via window.__dumpAuthLog().
 */

import { createUuid } from "@/core/utils/uuid.js";

const STORAGE_KEY = "__aimax_auth_debug_log__";
const TAB_ID_STORAGE_KEY = "__aimax_auth_debug_tab_id__";
const MAX_ENTRIES = 300;
const pageLoadId = createUuid();

function getTabId(): string | null {
  try {
    const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (existing) return existing;
    const tabId = createUuid();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
    return tabId;
  } catch {
    return null;
  }
}

type LogEntry = {
  t: string;
  tag: string;
  data?: unknown;
};

function readLog(): LogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LogEntry[]) : [];
  } catch {
    return [];
  }
}

function writeLog(entries: LogEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // localStorage full/unavailable — console still has it
  }
}

export function logAuthEvent(tag: string, data?: unknown): void {
  const entry: LogEntry = {
    t: new Date().toISOString(),
    tag,
    data: {
      ...(data && typeof data === 'object' ? data : { value: data }),
      tabId: getTabId(),
      pageLoadId,
      visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
    },
  };
  console.log(`[auth-debug] ${entry.t} ${tag}`, data ?? "");
  writeLog([...readLog(), entry]);
}

export function dumpAuthLog(): LogEntry[] {
  return readLog();
}

export function clearAuthLog(): void {
  localStorage.removeItem(STORAGE_KEY);
}

if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__dumpAuthLog = dumpAuthLog;
  (window as unknown as Record<string, unknown>).__clearAuthLog = clearAuthLog;
}
